import { Button } from "../button";
import { IcChevronLeft } from "@jetblue/kit-icons/ic-chevron-left";
import { toDateString } from "./date-input";
import { Calendar } from "../calendar";
import type { CalendarDate } from "@internationalized/date";
import type { ReactNode, RefObject } from "react";
import type { FooterProperties } from "./footer";
import { Footer } from "./footer";
import type { MonthProperties } from "../calendar/month";
import type { RenderDate } from "../calendar/day";
import { InputText } from "../input-text";
import type { CalendarHandle } from "../calendar/use-calendar-handle";
import { cn } from "../../utilities/tailwind";

type DateInputConfig = {
  label: string;
  value: CalendarDate | undefined;
  focused: boolean;
  onFocus: () => void;
};

export type MobileViewProperties = {
  calendarRef?: RefObject<CalendarHandle | null>;
  headingSlot: ReactNode;
  labels: { close: string; done: string; reset: string };
  startDate: CalendarDate;
  endDate: CalendarDate;
  onDateClick: (date: CalendarDate) => void;
  onCloseClick: () => void;
  onResetClick: () => void;
  dateInputConfigs: [DateInputConfig] | [DateInputConfig, DateInputConfig];
  isOpen: boolean;
  footerSlot?: FooterProperties["children"];
} & Omit<FooterProperties, "onDone"> &
  Pick<MonthProperties, "highlightRange"> & {
    renderDate?: RenderDate;
  };
export const MobileView = ({
  calendarRef,
  dateInputConfigs,
  endDate,
  footerSlot,
  headingSlot,
  highlightRange,
  isOpen,
  labels,
  onCloseClick,
  onDateClick,
  onResetClick,
  renderDate,
  startDate,
}: MobileViewProperties) => {
  if (!isOpen) {
    return null;
  }
  return (
    <div
      className="fixed start-0 top-0 z-100000 flex size-full flex-col bg-white md:hidden"
      tabIndex={-1}
    >
      <div className="flex flex-row items-center justify-between p-2">
        <Button
          aria-label={labels.close}
          onPress={onCloseClick}
          variant="minimal"
        >
          <IcChevronLeft className="size-4" />
        </Button>
        {headingSlot}
        <Button onPress={onResetClick} variant="minimal">
          {labels.reset}
        </Button>
      </div>
      <div className="p-4">
        <div className="flex flex-row gap-1">
          {dateInputConfigs.map(({ label, value, focused, onFocus }) => (
            <InputText
              className={cn(focused && "border-royal-blue!")}
              data-has-fake-focus={focused}
              inputClassName={cn(focused && "border-royal-blue!")}
              isReadOnly
              key={label}
              label={label}
              onFocus={onFocus}
              value={toDateString(value)}
            />
          ))}
        </div>
      </div>
      <Calendar
        calendarRef={calendarRef}
        endDate={endDate}
        highlightRange={highlightRange}
        onDateClick={onDateClick}
        rangeEnd={dateInputConfigs[1]?.value}
        rangeStart={dateInputConfigs[0].value}
        renderDate={renderDate}
        startDate={startDate}
      />

      <Footer labels={{ done: labels.done }} onDone={onCloseClick}>
        {footerSlot}
      </Footer>
    </div>
  );
};
MobileView.displayName = "DatePicker.MobileView";
